import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _excluded = ["doesNotMeetMAUCriteria", "attributes"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { useCallback, useContext } from 'react';
import { match, P } from 'ts-pattern';
import { FabricChannel } from '@atlaskit/analytics-listeners';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { AIEventsInstrumentationContext } from '../context';
import { defaultAIEventsInstrumentationConfigValues } from '../context/model';
import { getTagsWithPlatfromTag } from '../effects/getTagsWithPlatfromTag';
import { AISessionState } from '../events';
import { useAIInstrumentationSetup } from './useAIInstrumentationSetup';
export var useAIEventsInstrumentation = function useAIEventsInstrumentation(customConfig) {
  var context = useContext(AIEventsInstrumentationContext);
  if (customConfig) {
    context = _objectSpread({
      config: _objectSpread(_objectSpread({}, defaultAIEventsInstrumentationConfigValues), customConfig)
    }, useAIInstrumentationSetup(customConfig.singleInstrumentationID, customConfig.cookieSingleInstrumentationConfig));
  }
  var _useAnalyticsEvents = useAnalyticsEvents(),
    createAnalyticsEvent = _useAnalyticsEvents.createAnalyticsEvent;
  if (context === null) {
    throw new Error('useAIEventsInstrumentation must be used within a AIEventsInstrumentationProvider');
  }
  var _context = context,
    _context$config = _context.config,
    source = _context$config.source,
    aiExperienceName = _context$config.aiExperienceName,
    aiFeatureName = _context$config.aiFeatureName,
    proactiveGeneratedAI = _context$config.proactiveGeneratedAI,
    userGeneratedAI = _context$config.userGeneratedAI,
    isAIFeature = _context$config.isAIFeature,
    channel = _context$config.channel,
    subproduct = _context$config.subproduct,
    actionSubjectId = _context$config.actionSubjectId,
    tags = _context$config.tags,
    product = _context$config.product,
    eventDispatcherSource = _context$config.eventDispatcherSource,
    skipAISessionValidation = _context$config.skipAISessionValidation,
    customAnalyticsFire = _context$config.customAnalyticsFire,
    additionalAttributes = _context$config.additionalAttributes,
    useDefaultGASv3EventSchema = _context$config.useDefaultGASv3EventSchema,
    enablePlatformEvents = _context$config.enablePlatformEvents,
    refreshSingleInstrumentationID = _context.refreshSingleInstrumentationID,
    getSingleInstrumentationID = _context.getSingleInstrumentationID,
    setAISessionState = _context.setAISessionState,
    getAISessionState = _context.getAISessionState;
  var validateAndUpdateAISession = useCallback(function (nextState) {
    var currentAISessionState = getAISessionState();
    return match({
      currentState: currentAISessionState,
      nextState: nextState,
      skipAISessionValidation: skipAISessionValidation
    }).with({
      skipAISessionValidation: true
    }, function () {
      return true;
    }).with({
      currentState: AISessionState.unset,
      nextState: P.union(AISessionState.viewed, AISessionState.error, AISessionState.dismissed)
    }, function () {
      // eslint-disable-next-line no-console
      console.error("[@atlassian/ai-analytics]: ".concat(nextState, " event should be called only after trackAIInteractionInit()"));
      return false;
    }).with({
      currentState: AISessionState.initiated,
      nextState: P.union(AISessionState.submitted, AISessionState.actioned, AISessionState.discarded)
    }, function () {
      // eslint-disable-next-line no-console
      console.error("[@atlassian/ai-analytics]: ".concat(nextState, " event should be called only after trackAIResultView()"));
      return false;
    }).otherwise(function () {
      setAISessionState(nextState);
      return true;
    });
  }, [setAISessionState, getAISessionState, skipAISessionValidation]);
  var fireEventWithOptionalChannel = useCallback(function (event) {
    if (typeof customAnalyticsFire === 'function') {
      customAnalyticsFire(event, createAnalyticsEvent({}));
      return;
    }
    if (enablePlatformEvents) {
      // it goes thru the aiMate analytics listener to avoid validation per prodcut listener
      // https://bitbucket.org/atlassian/atlassian-frontend-mirror/src/f904f690cf57ef84acad6c7bf1b5bd90ad9cada2/analytics/analytics-listeners/src/atlaskit/process-event.ts#lines-58
      createAnalyticsEvent(event).fire(FabricChannel.aiMate);
      return;
    }
    if (channel) {
      createAnalyticsEvent(event).fire(channel);
      return;
    }
    createAnalyticsEvent(event).fire();
  }, [channel, customAnalyticsFire, createAnalyticsEvent, enablePlatformEvents]);
  var createAndMergeEventPayload = useCallback(function (payload, partialPayload) {
    var _partialPayload$actio;
    var singleInstrumentationID = getSingleInstrumentationID();
    var eventPayload = {
      action: payload.action,
      actionSubject: payload.actionSubject,
      actionSubjectId: (_partialPayload$actio = partialPayload === null || partialPayload === void 0 ? void 0 : partialPayload.actionSubjectId) !== null && _partialPayload$actio !== void 0 ? _partialPayload$actio : actionSubjectId,
      attributes: _objectSpread(_objectSpread(_objectSpread({
        aiExperienceName: aiExperienceName,
        aiFeatureName: aiFeatureName,
        proactiveGeneratedAI: proactiveGeneratedAI,
        userGeneratedAI: userGeneratedAI,
        isAIFeature: isAIFeature,
        eventDispatcherSource: eventDispatcherSource,
        singleInstrumentationID: singleInstrumentationID
      }, payload.attributes), partialPayload === null || partialPayload === void 0 ? void 0 : partialPayload.attributes), additionalAttributes)
    };
    return match({
      product: product,
      channel: channel,
      useDefaultGASv3EventSchema: useDefaultGASv3EventSchema,
      enablePlatformEvents: enablePlatformEvents
    }).with({
      enablePlatformEvents: true
    }, function () {
      var _partialPayload$sourc;
      return _objectSpread(_objectSpread({
        eventType: 'track'
      }, eventPayload), {}, {
        tags: getTagsWithPlatfromTag(tags),
        source: (_partialPayload$sourc = partialPayload === null || partialPayload === void 0 ? void 0 : partialPayload.source) !== null && _partialPayload$sourc !== void 0 ? _partialPayload$sourc : source,
        subproduct: subproduct,
        product: product
      });
    }).with({
      product: 'confluence',
      channel: P.nullish,
      useDefaultGASv3EventSchema: false
    }, function () {
      var _partialPayload$tags, _partialPayload$sourc2;
      // confluence event validation fronted logic
      // https://bitbucket.org/atlassian/atlassian-frontend-monorepo/src/f8bf8ba8c7b54c6e2764d85551b3f989b69d28d8/confluence/next/packages/analytics/src/analyticsListener.tsx#lines-53
      return {
        type: 'sendTrackEvent',
        data: _objectSpread(_objectSpread({}, eventPayload), {}, {
          tags: (_partialPayload$tags = partialPayload === null || partialPayload === void 0 ? void 0 : partialPayload.tags) !== null && _partialPayload$tags !== void 0 ? _partialPayload$tags : tags,
          source: (_partialPayload$sourc2 = partialPayload === null || partialPayload === void 0 ? void 0 : partialPayload.source) !== null && _partialPayload$sourc2 !== void 0 ? _partialPayload$sourc2 : source,
          subproduct: subproduct
        })
      };
    }).with({
      product: 'jira',
      channel: P.nullish,
      useDefaultGASv3EventSchema: false
    }, function () {
      // jira event validation fronted logic
      // https://bitbucket.org/atlassian/atlassian-frontend-monorepo/src/f8bf8ba8c7b54c6e2764d85551b3f989b69d28d8/jira/src/packages/platform/observability/product-analytics-bridge/src/utils/extract-data-from-event.tsx#lines-164
      // it requires to use no more than 4 properties on the top level of event shape for custom event fire.
      return eventPayload;
    }).otherwise(function () {
      var _partialPayload$tags2, _partialPayload$sourc3;
      // for any other product type we should use the default event shape
      // also if there is a custom event channel, we also should use the default event shape
      return _objectSpread(_objectSpread({
        eventType: 'track'
      }, eventPayload), {}, {
        tags: (_partialPayload$tags2 = partialPayload === null || partialPayload === void 0 ? void 0 : partialPayload.tags) !== null && _partialPayload$tags2 !== void 0 ? _partialPayload$tags2 : tags,
        source: (_partialPayload$sourc3 = partialPayload === null || partialPayload === void 0 ? void 0 : partialPayload.source) !== null && _partialPayload$sourc3 !== void 0 ? _partialPayload$sourc3 : source,
        subproduct: subproduct,
        product: product
      });
    });
  }, [subproduct, channel, aiExperienceName, aiFeatureName, product, proactiveGeneratedAI, getSingleInstrumentationID, useDefaultGASv3EventSchema, userGeneratedAI, isAIFeature, source, tags, eventDispatcherSource, actionSubjectId, additionalAttributes, enablePlatformEvents]);
  var trackAIInteractionInit = useCallback(function (payload) {
    var _payload$attributes;
    if (!validateAndUpdateAISession(AISessionState.initiated)) {
      return;
    }
    refreshSingleInstrumentationID(payload === null || payload === void 0 || (_payload$attributes = payload.attributes) === null || _payload$attributes === void 0 ? void 0 : _payload$attributes.singleInstrumentationID);
    var event = createAndMergeEventPayload({
      action: 'initiated',
      actionSubject: 'aiInteraction'
    }, payload);
    fireEventWithOptionalChannel(event);
  }, [refreshSingleInstrumentationID, createAndMergeEventPayload, fireEventWithOptionalChannel, validateAndUpdateAISession]);
  var trackAIResultView = useCallback(function (payload) {
    if (!validateAndUpdateAISession(AISessionState.viewed)) {
      return;
    }
    var _ref = payload || {},
      doesNotMeetMAUCriteria = _ref.doesNotMeetMAUCriteria,
      attributes = _ref.attributes,
      payloadRest = _objectWithoutProperties(_ref, _excluded);
    var event = createAndMergeEventPayload({
      action: 'viewed',
      actionSubject: 'aiResult'
    }, _objectSpread(_objectSpread({}, payloadRest), {}, {
      attributes: _objectSpread(_objectSpread({}, attributes), {}, {
        doesNotMeetMAUCriteria: !!doesNotMeetMAUCriteria
      })
    }));
    fireEventWithOptionalChannel(event);
  }, [validateAndUpdateAISession, createAndMergeEventPayload, fireEventWithOptionalChannel]);
  var trackAIResultError = useCallback(function (error, payload) {
    if (!validateAndUpdateAISession(AISessionState.error)) {
      return;
    }
    var event = createAndMergeEventPayload({
      action: 'error',
      actionSubject: 'aiResult',
      attributes: _objectSpread({}, error)
    }, payload);
    fireEventWithOptionalChannel(event);
  }, [validateAndUpdateAISession, createAndMergeEventPayload, fireEventWithOptionalChannel]);
  var trackAIInteractionDismiss = useCallback(function (payload) {
    if (!validateAndUpdateAISession(AISessionState.dismissed)) {
      return;
    }
    var event = createAndMergeEventPayload({
      action: 'dismissed',
      actionSubject: 'aiInteraction'
    }, payload);
    fireEventWithOptionalChannel(event);
  }, [validateAndUpdateAISession, createAndMergeEventPayload, fireEventWithOptionalChannel]);
  var trackAIResultAction = useCallback(function (aiResultAction, payload) {
    if (!validateAndUpdateAISession(AISessionState.actioned)) {
      return;
    }
    var event = createAndMergeEventPayload({
      action: 'actioned',
      actionSubject: 'aiResult',
      attributes: {
        aiResultAction: aiResultAction
      }
    }, payload);
    fireEventWithOptionalChannel(event);
  }, [validateAndUpdateAISession, createAndMergeEventPayload, fireEventWithOptionalChannel]);
  var trackAIResultDiscarded = useCallback(function (aiResultAction, payload) {
    if (!validateAndUpdateAISession(AISessionState.discarded)) {
      return;
    }
    var event = createAndMergeEventPayload({
      action: 'actioned',
      actionSubject: 'discarded',
      attributes: {
        aiResultAction: aiResultAction
      }
    }, payload);
    fireEventWithOptionalChannel(event);
  }, [validateAndUpdateAISession, createAndMergeEventPayload, fireEventWithOptionalChannel]);
  var trackAIFeedbackSubmit = useCallback(function (aiFeedbackResult, payload) {
    if (!validateAndUpdateAISession(AISessionState.submitted)) {
      return;
    }
    var event = createAndMergeEventPayload({
      action: 'submitted',
      actionSubject: 'aiFeedback',
      attributes: {
        aiFeedbackResult: aiFeedbackResult
      }
    }, payload);
    fireEventWithOptionalChannel(event);
  }, [validateAndUpdateAISession, createAndMergeEventPayload, fireEventWithOptionalChannel]);
  return {
    trackAIInteractionInit: trackAIInteractionInit,
    trackAIResultView: trackAIResultView,
    trackAIResultError: trackAIResultError,
    trackAIResultDiscarded: trackAIResultDiscarded,
    trackAIInteractionDismiss: trackAIInteractionDismiss,
    trackAIResultAction: trackAIResultAction,
    trackAIFeedbackSubmit: trackAIFeedbackSubmit,
    refreshSingleInstrumentationID: refreshSingleInstrumentationID
  };
};